import { Link } from "react-router-dom";
import { Fragment } from "react";
import { ArrowRight, BookOpen, Scale, Coins, Wallet, Layers, Landmark, ShieldAlert } from "lucide-react";
import { Article } from "@/lib/types";
import { timeAgo } from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCard";
import { NativeAdCard } from "@/components/AdSlot";
import { useArticlePath } from "@/lib/i18n";
import { sectorTrack } from "./SectorHero";

const SectionHead = ({ title, tag, cta, ctaTo, testid, accent = "text-white", sector = "crypto" }: {
  title: string; tag?: string; cta?: string; ctaTo?: string; testid: string; accent?: string; sector?: string;
}) => (
  <div className="flex items-baseline justify-between gap-3 mb-4" data-testid={testid}>
    <h2 className={`font-heading font-bold text-lg ${accent}`}>
      {title}
      {tag && <span className="ml-3 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-[#A78BFA] align-middle">{tag}</span>}
    </h2>
    {cta && ctaTo && (
      <Link to={ctaTo} onClick={() => sectorTrack("section_view_all", testid, sector)}
        className="group font-mono text-[11px] font-bold text-[#00E5FF] hover:text-white transition-colors whitespace-nowrap">
        {cta} <ArrowRight className="inline h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" />
      </Link>
    )}
  </div>
);

export const LatestCrypto = ({ items, title = "Latest Crypto", testid = "latest-crypto", sector = "crypto" }: {
  items: Article[]; title?: string; testid?: string; sector?: string;
}) => {
  if (!items?.length) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10" data-testid={testid}>
      <SectionHead title={title} cta="View all" ctaTo="/latest" testid={`${testid}-head`} sector={sector} />
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-5 ${
        items.length >= 3 ? "lg:grid-cols-3" : ""}`}>
        {items.map((a, i) => (
          <Fragment key={a.slug}>
            <ArticleCard article={a} />
            {i === Math.min(1, items.length - 1) && <NativeAdCard placement="sector_inline" />}
          </Fragment>
        ))}
      </div>
    </section>
  );
};

const OriginalBadge = () => (
  <span className="pointer-events-none absolute top-3 left-3 z-10 rounded border border-[#7C3AED]/70 bg-[#0b0c10]/85 backdrop-blur-sm px-2 py-1 font-mono text-[8.5px] font-bold uppercase tracking-[0.2em] text-[#A78BFA]">
    NewsLayer Original
  </span>
);

// Adaptive geometry: composition follows real inventory (1 featured / 2 / 3 / 4)
export const AdaptiveCards = ({ items, badge }: { items: Article[]; badge?: boolean }) => {
  if (items.length === 1) {
    return (
      <div className="lg:w-[58%] relative">
        {badge && <OriginalBadge />}
        <ArticleCard article={items[0]} variant="featured" />
      </div>
    );
  }
  return (
    <div className={`grid grid-cols-1 gap-5 ${
      items.length === 2 ? "sm:grid-cols-2"
        : items.length === 3 ? "sm:grid-cols-2 lg:grid-cols-3"
          : "sm:grid-cols-2 lg:grid-cols-4"}`}>
      {items.map((a) => (
        <div key={a.slug} className="relative">
          {badge && <OriginalBadge />}
          <ArticleCard article={a} />
        </div>
      ))}
    </div>
  );
};

export const OriginalsSection = ({ items, title = "Crypto Originals", testid = "crypto-originals", sector = "crypto" }: {
  items: Article[]; title?: string; testid?: string; sector?: string;
}) => {
  if (!items?.length) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10" data-testid={testid}>
      <SectionHead title={title} tag="NewsLayer Editorial" cta="All originals" ctaTo="/originals" testid={`${testid}-head`} sector={sector} />
      <AdaptiveCards items={items} badge />
    </section>
  );
};

export const ResearchSection = ({ items, testid = "crypto-research", sector = "crypto" }: {
  items: Article[]; testid?: string; sector?: string;
}) => {
  if (!items?.length) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10" data-testid={testid}>
      <SectionHead title="Research & Deep Dives" cta="All research" ctaTo="/research" testid={`${testid}-head`} sector={sector} />
      <AdaptiveCards items={items} />
    </section>
  );
};

const EXPLAINER_ICON = (title: string) => {
  const t = title.toLowerCase();
  if (/stablecoin|tether|usdc/.test(t)) return Coins;
  if (/wallet|custody|seed/.test(t)) return Wallet;
  if (/sec|regulat|law|policy|legislat/.test(t)) return Scale;
  if (/etf|institution|fund|treasur/.test(t)) return Landmark;
  if (/security|hack|scam|exploit/.test(t)) return ShieldAlert;
  if (/layer|chain|protocol|token|staking|defi/.test(t)) return Layers;
  return BookOpen;
};

export const ExplainersRail = ({ items, title = "Crypto, Explained", testid = "crypto-explainers", sector = "crypto", embedded = false }: {
  items: { title: string; slug: string; read_minutes?: number }[]; title?: string; testid?: string; sector?: string; embedded?: boolean;
}) => {
  const toArticle = useArticlePath();
  if (!items?.length) return null;
  return (
    <section className={embedded ? "" : "max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10"} data-testid={testid}>
      <SectionHead title={title} cta="All explainers" ctaTo="/explainers" testid={`${testid}-head`} sector={sector} />
      <div className={embedded ? "grid grid-cols-1 gap-3" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"}>
        {items.map((e) => {
          const Icon = EXPLAINER_ICON(e.title);
          return (
            <Link key={e.slug} to={toArticle(e.slug)} data-testid={`explainer-${e.slug}`}
              onClick={() => sectorTrack("explainer_click", e.slug, sector)}
              className="group flex items-center gap-3.5 rounded-xl border border-white/[0.08] bg-[#0b0c10] px-4 py-3.5 hover:border-[#34D399]/40 transition-colors">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#34D399]/[0.12] text-[#34D399]">
                <Icon aria-hidden className="h-[17px] w-[17px]" />
              </span>
              <span className="min-w-0">
                <span className="block font-news font-bold text-white text-[14px] leading-snug line-clamp-2 group-hover:text-[#34D399] transition-colors">
                  {e.title}
                </span>
                {e.read_minutes ? (
                  <span className="block font-mono text-[9.5px] text-gray-500 mt-1">{e.read_minutes} min read</span>
                ) : null}
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

const STATUS_STYLE: Record<string, string> = {
  enforced: "text-[#34D399] border-[#34D399]/40 bg-[#34D399]/10",
  enacted: "text-[#34D399] border-[#34D399]/40 bg-[#34D399]/10",
  in_progress: "text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10",
  proposed: "text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/10",
};

export const RegulationSection = ({ items, title = "Regulation Affecting Crypto", testid = "crypto-regulation", sector = "crypto", embedded = false }: {
  items: any[]; title?: string; testid?: string; sector?: string; embedded?: boolean;
}) => {
  if (!items?.length) return null;
  return (
    <section className={embedded ? "" : "max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10"} data-testid={testid}>
      <SectionHead title={title} cta="Open Regulation Tracker" ctaTo="/regulation-tracker" testid={`${testid}-head`} sector={sector} />
      <div className={embedded ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"}>
        {items.map((r) => (
          <Link key={r.id} to={r.slug ? `/regulation/${r.slug}` : "/regulation-tracker"}
            data-testid={`regulation-card-${r.id}`}
            onClick={() => sectorTrack("regulation_click", r.slug || r.id, sector)}
            className="group flex flex-col rounded-xl border border-white/[0.08] bg-[#0b0c10] p-4 hover:border-[#F59E0B]/40 transition-colors">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[9.5px] font-bold uppercase tracking-[0.18em] text-gray-400 truncate">
                {r.jurisdiction || "Global"}
              </span>
              {r.status && (
                <span className={`shrink-0 rounded-full border px-2 py-0.5 font-mono text-[8.5px] font-bold uppercase tracking-wider ${
                  STATUS_STYLE[r.status] || "text-gray-400 border-white/20 bg-white/5"}`}>
                  {String(r.status).replace("_", " ")}
                </span>
              )}
            </div>
            <p className="font-news font-bold text-white text-[14.5px] leading-snug mt-2.5 line-clamp-3 group-hover:text-[#F59E0B] transition-colors">
              {r.title}
            </p>
            {r.updated_at && (
              <p className="font-mono text-[9.5px] text-gray-500 mt-auto pt-3">Updated {timeAgo(r.updated_at)}</p>
            )}
          </Link>
        ))}
      </div>
    </section>
  );
};
